import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import type { Condo } from '@/api/pending'
import { useAccess } from '@/access/useAccess'
import { SUBTITLES } from '@/shell/sectionCopy'
import {
  Avatar,
  Button,
  Card,
  Eyebrow,
  Field,
  Icon,
  IconButton,
  PageHeader,
  Row,
  SectionTitle,
  Spinner,
  useToast,
  type IconName,
} from '@/ui'
import { ICON_CODEPOINTS } from '@/ui/iconNames'
import {
  condoQuery,
  useDeleteContact,
  useDeleteRule,
  useSaveContact,
  useSaveRule,
  useUpdateCondo,
} from './api'
import { ContactModal, type ContactDraft } from './ContactModal'
import { RuleModal, type RuleDraft } from './RuleModal'
import { S } from './strings'
import styles from './CondoView.module.css'

const asIcon = (name: string): IconName => (name in ICON_CODEPOINTS ? (name as IconName) : 'flag')

function Locked({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.locked}>
      <span className={styles.lockedLabel}>{label}</span>
      <div className={styles.lockedBox}>
        <Icon name="lock" size={16} />
        <span className={styles.lockedValue}>{value}</span>
      </div>
    </div>
  )
}

function ReadRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.readRow}>
      <Eyebrow>{label}</Eyebrow>
      <span className={styles.readValue}>{value || '—'}</span>
    </div>
  )
}

type Form = Pick<Condo, 'email' | 'website' | 'history'>
const formOf = (c: Condo): Form => ({ email: c.email, website: c.website, history: c.history })

export function CondoView() {
  const { condoId, membership } = useAccess()
  const { data: condo, isPending } = useQuery(condoQuery(condoId))
  return (
    <>
      <PageHeader
        title="Condomínio"
        subtitle={SUBTITLES.condo({ condoName: membership.condominium_name, waiting: 0 })}
      />
      {isPending || !condo ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.grid}>
          <div className={styles.column}>
            <IdentificationCard condo={condo} />
            <EditableCard condoId={condoId} condo={condo} />
          </div>
          <div className={styles.column}>
            <RulesCard condoId={condoId} condo={condo} />
            <ContactsCard condoId={condoId} condo={condo} />
          </div>
        </div>
      )}
    </>
  )
}

function IdentificationCard({ condo }: { condo: Condo }) {
  return (
    <Card className={styles.stack}>
      <SectionTitle>{S.identification}</SectionTitle>
      <Locked label={S.name} value={condo.name} />
      <Locked label={S.address} value={condo.address} />
      <div className={styles.pair}>
        <Locked label={S.phone} value={condo.phone} />
        <Locked label={condo.tax_type} value={condo.tax_id} />
      </div>
    </Card>
  )
}

function EditableCard({ condoId, condo }: { condoId: number; condo: Condo }) {
  const toast = useToast()
  const update = useUpdateCondo(condoId)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<Form>(formOf(condo))
  const set = (k: keyof Form) => (v: string) => setForm((s) => ({ ...s, [k]: v }))
  const saved = formOf(condo)
  const dirty =
    form.email !== saved.email || form.website !== saved.website || form.history !== saved.history

  function startEdit() {
    setForm(formOf(condo))
    update.reset()
    setEditing(true)
  }
  function save() {
    update.mutate(form, {
      onSuccess: () => {
        toast(S.saved)
        setEditing(false)
      },
    })
  }

  return (
    <Card className={styles.stack}>
      <SectionTitle
        action={
          editing ? (
            <div className={styles.actions}>
              <Button size="s" variant="ghost" onClick={() => setEditing(false)}>
                {S.cancel}
              </Button>
              <Button
                size="s"
                icon="check"
                disabled={!dirty}
                loading={update.isPending}
                onClick={save}
              >
                {S.save}
              </Button>
            </div>
          ) : (
            <Button size="s" variant="secondary" icon="edit" onClick={startEdit}>
              {S.edit}
            </Button>
          )
        }
      >
        {S.editable}
      </SectionTitle>
      {editing ? (
        <>
          <Field label={S.email} value={form.email} onChange={set('email')} type="email" />
          <Field label={S.website} value={form.website} onChange={set('website')} />
          <Field
            label={S.history}
            value={form.history}
            onChange={set('history')}
            multiline
            hint={S.historyHint}
            error={update.isError ? S.saveError : undefined}
          />
        </>
      ) : (
        <>
          <ReadRow label={S.email} value={condo.email} />
          <ReadRow label={S.website} value={condo.website} />
          <ReadRow label={S.history} value={condo.history} />
        </>
      )}
    </Card>
  )
}

function RulesCard({ condoId, condo }: { condoId: number; condo: Condo }) {
  const [draft, setDraft] = useState<RuleDraft | null>(null)
  const save = useSaveRule(condoId)
  const remove = useDeleteRule(condoId)
  return (
    <Card pad={0}>
      <div className={styles.cardHead}>
        <SectionTitle
          action={
            <Button
              size="s"
              variant="secondary"
              icon="add"
              onClick={() => {
                save.reset()
                setDraft({ icon: 'schedule', title: '', description: '' })
              }}
            >
              {S.addRule}
            </Button>
          }
        >
          {S.rules}
        </SectionTitle>
      </div>
      {condo.rules.map((r) => (
        <Row key={r.id} className={styles.top}>
          <div className={styles.ruleIcon}>
            <Icon name={asIcon(r.icon)} size={17} />
          </div>
          <div className={styles.grow}>
            <div className={styles.itemTitle}>{r.title}</div>
            <div className={styles.itemDesc}>{r.description}</div>
          </div>
          <div className={styles.actions}>
            <IconButton
              icon="edit"
              aria-label={S.editRule(r.title)}
              onClick={() => {
                save.reset()
                setDraft(r)
              }}
            />
            <IconButton
              icon="delete"
              aria-label={S.deleteRule(r.title)}
              onClick={() => remove.mutate(r.id)}
            />
          </div>
        </Row>
      ))}
      {draft && (
        <RuleModal
          initial={draft}
          pending={save.isPending}
          error={save.isError}
          onClose={() => setDraft(null)}
          onSave={(d) => save.mutate(d, { onSuccess: () => setDraft(null) })}
        />
      )}
    </Card>
  )
}

function ContactsCard({ condoId, condo }: { condoId: number; condo: Condo }) {
  const [draft, setDraft] = useState<ContactDraft | null>(null)
  const save = useSaveContact(condoId)
  const remove = useDeleteContact(condoId)
  return (
    <Card pad={0}>
      <div className={styles.cardHead}>
        <SectionTitle
          action={
            <Button
              size="s"
              variant="secondary"
              icon="add"
              onClick={() => {
                save.reset()
                setDraft({ name: '', role: '', phone: '' })
              }}
            >
              {S.addContact}
            </Button>
          }
        >
          {S.contacts}
        </SectionTitle>
      </div>
      {condo.contacts.map((c) => (
        <Row key={c.id}>
          <Avatar name={c.name} />
          <div className={styles.grow}>
            <div className={styles.itemTitle}>{c.name}</div>
            <div className={styles.itemMeta}>
              {c.role} · {c.phone}
            </div>
          </div>
          <div className={styles.actions}>
            <IconButton
              icon="edit"
              aria-label={S.editContact(c.name)}
              onClick={() => {
                save.reset()
                setDraft(c)
              }}
            />
            <IconButton
              icon="delete"
              aria-label={S.deleteContact(c.name)}
              onClick={() => remove.mutate(c.id)}
            />
          </div>
        </Row>
      ))}
      {draft && (
        <ContactModal
          initial={draft}
          pending={save.isPending}
          error={save.isError}
          onClose={() => setDraft(null)}
          onSave={(d) => save.mutate(d, { onSuccess: () => setDraft(null) })}
        />
      )}
    </Card>
  )
}
